import { Link } from "react-router";
import { IndianRupee, TrendingUp, Package, ClipboardList, AlertCircle } from "lucide-react";
import { DashboardCards, TableLayout, type TableColumn } from "../../components/dashboard";
import { InventoryAlerts } from "../components/dashboard/InventoryAlerts";
import { RecentActivity } from "../components/dashboard/RecentActivity";
import { QuickActions } from "../components/dashboard/QuickActions";
import { POSQuickAccess } from "../components/dashboard/POSQuickAccess";
import { Alert, AlertDescription, AlertTitle } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { useAuth } from "../../providers/AuthProvider";
import { api } from "../../lib/api";
import { formatInr, formatInrIntl, formatInrLoose, formatInrThousandsTick } from "../../lib/currency";
import { toYmd } from "../../lib/isoWeekRanges";
import { useEffect, useMemo, useState } from "react";
import type { DashboardCardItem } from "../../components/dashboard/DashboardCards";
import type { BusinessDashboardSummaryResponse } from "../../lib/api";
import { useQuery } from "@tanstack/react-query";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { mapRecentActivityToFeed } from "../components/dashboard/mapRecentActivityFeed";
import type { ActivityFeedItem } from "../components/dashboard/mapRecentActivityFeed";
import { ReportsDateRangeFilterBar } from "../components/reports/ReportsDateRangeFilterBar";

export function DashboardPage() {
  const { needsProfileCompletion, profileGate, token } = useAuth();
  type Row = {
    orderId: string;
    customer: string;
    amount: string;
    status: "Paid" | "Pending" | "Overdue";
  };

  const lowStockThreshold = 20;

  const [cards, setCards] = useState<DashboardCardItem[]>([
    {
      title: "Today's Sales",
      value: formatInr(0),
      icon: IndianRupee,
      trend: { value: 0, isPositive: true },
      trendCompareLabel: "vs yesterday",
      iconColor: "bg-emerald-600",
    },
    {
      title: "Total Revenue",
      value: formatInr(0),
      icon: TrendingUp,
      trend: { value: 0, isPositive: true },
      trendCompareLabel: "vs prior week",
      iconColor: "bg-blue-600",
    },
    {
      title: "Low Stock Items",
      value: "0",
      icon: Package,
      iconColor: "bg-amber-600",
    },
    {
      title: "Pending Orders",
      value: "0",
      icon: ClipboardList,
      trend: { value: 0, isPositive: true },
      trendCompareLabel: "vs prior week",
      iconColor: "bg-purple-600",
    },
  ]);
  const [recentOrders, setRecentOrders] = useState<Row[]>([]);
  const [salesTrendRows, setSalesTrendRows] = useState<Array<{ date: string; total_sales: string }>>([]);
  const [salesTrendLast7, setSalesTrendLast7] = useState<Array<{ date: string; total_sales: string }>>([]);
  const [salesTrendLast30, setSalesTrendLast30] = useState<Array<{ date: string; total_sales: string }>>([]);
  const [salesOverviewMode, setSalesOverviewMode] = useState<"weekly" | "monthly" | "range">("weekly");
  const [salesRange, setSalesRange] = useState<{ fromDate: string | null; toDate: string | null }>({
    fromDate: null,
    toDate: null,
  });
  // Keep these initialized so dashboard widgets don't fire their own fallback API calls
  // while the consolidated dashboard request is still in-flight.
  const [posSnapshot, setPosSnapshot] = useState<{ todayOrders: number; todayRevenueFormatted: string }>({
    todayOrders: 0,
    todayRevenueFormatted: formatInrIntl(0),
  });
  const [inventoryAlertProps, setInventoryAlertProps] = useState<{
    samples: Array<{ id: string; name: string; currentStock: number; minStock: number; severity: "critical" | "warning" }>;
  }>({ samples: [] });
  const [activityFeed, setActivityFeed] = useState<ActivityFeedItem[]>([]);

  const dashboardQuery = useQuery({
    queryKey: ["dashboard", "business", token],
    enabled: Boolean(token),
    queryFn: async () => {
      if (!token) throw new Error("missing token");
      return await api.dashboard.getSummary({ token });
    },
    staleTime: 30_000,
    gcTime: 5 * 60_000,
  });

  function money(v: string | number | null | undefined) {
    const n = typeof v === "number" ? v : Number(v ?? 0);
    if (!Number.isFinite(n)) return formatInr(0);
    return formatInrIntl(n);
  }

  function pctDelta(curr: number, prev: number) {
    if (prev === 0) return { value: 0, isPositive: true };
    const pct = ((curr - prev) / prev) * 100;
    return { value: Math.round(pct * 10) / 10, isPositive: pct >= 0 };
  }

  const datePlan = useMemo(() => {
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    return {
      today: toYmd(now),
      yesterday: toYmd(yesterday),
    };
  }, []);

  const tableColumns: TableColumn<Row>[] = [
    {
      key: "orderId",
      header: "Order",
      render: (row: Row) => <span className="font-medium">{row.orderId}</span>,
    },
    {
      key: "customer",
      header: "Customer",
      render: (row: Row) => <span className="text-muted-foreground">{row.customer}</span>,
    },
    {
      key: "amount",
      header: "Amount",
      render: (row: Row) => <span>{row.amount}</span>,
    },
    {
      key: "status",
      header: "Status",
      render: (row: Row) => (
        <span
          className={[
            row.status === "Paid" ? "text-emerald-700" : "",
            row.status === "Pending" ? "text-amber-700" : "",
            row.status === "Overdue" ? "text-destructive" : "",
          ].join(" ")}
        >
          {row.status}
        </span>
      ),
    },
  ];

  useEffect(() => {
    if (!dashboardQuery.data) return;

    let cancelled = false;
    (async () => {
      try {
        const json = dashboardQuery.data as BusinessDashboardSummaryResponse;
        const d = json.data;
        const last30 = d.sales_trend.last_30_days ?? [];

        const todaySales = Number(d.revenue_snapshot.today_sales ?? 0);
        const ydayPoint = last30.find((r) => r.date === datePlan.yesterday);
        const ydaySales = Number(ydayPoint?.total_sales ?? 0);
        const todayTrend = pctDelta(todaySales, ydaySales);

        const thirtyTotal = last30.reduce((s, r) => s + Number(r.total_sales ?? 0), 0);

        const curRev = Number(d.revenue_comparison.current_total_revenue ?? 0);
        const prevRev = Number(d.revenue_comparison.previous_total_revenue ?? 0);
        const revenueTrend = pctDelta(curRev, prevRev);

        const lowCount = d.alerts.low_stock.count;
        const outCount = d.alerts.out_of_stock.count;

        const pendingOrdersCount = d.purchase_orders.pending_total ?? 0;
        const pendingThisWeek = d.purchase_orders.pending_current_period ?? 0;
        const pendingPrevWeek = d.purchase_orders.pending_previous_period ?? 0;
        const pendingTrend = pctDelta(pendingThisWeek, pendingPrevWeek);

        setCards([
          {
            title: "Today's Sales",
            value: money(d.revenue_snapshot.today_sales),
            icon: IndianRupee,
            trend: { value: todayTrend.value, isPositive: todayTrend.isPositive },
            trendCompareLabel: "vs yesterday",
            iconColor: "bg-emerald-600",
          },
          {
            title: "Total Revenue",
            value: money(thirtyTotal),
            icon: TrendingUp,
            trend: { value: revenueTrend.value, isPositive: revenueTrend.isPositive },
            trendCompareLabel: "vs prior week",
            iconColor: "bg-blue-600",
          },
          {
            title: "Low Stock Items",
            value: String(lowCount + outCount),
            icon: Package,
            iconColor: "bg-amber-600",
          },
          {
            title: "Pending Orders",
            value: String(pendingOrdersCount),
            icon: ClipboardList,
            trend: { value: pendingTrend.value, isPositive: pendingTrend.isPositive },
            trendCompareLabel: "vs prior week",
            iconColor: "bg-purple-600",
          },
        ]);

        setPosSnapshot({
          todayOrders: d.revenue_snapshot.total_orders,
          todayRevenueFormatted: money(d.revenue_snapshot.today_sales),
        });

        const outSamples = d.alerts.out_of_stock.samples.map((s, idx) => ({
          id: `out_${s.product_id}_${idx}`,
          name: s.product_name,
          currentStock: Number(s.quantity_remaining ?? 0),
          minStock: lowStockThreshold,
          severity: "critical" as const,
        }));
        const lowSamples = d.alerts.low_stock.samples.map((s, idx) => ({
          id: `low_${s.product_id}_${idx}`,
          name: s.product_name,
          currentStock: Number(s.quantity_remaining ?? 0),
          minStock: lowStockThreshold,
          severity: "warning" as const,
        }));
        setInventoryAlertProps({ samples: [...outSamples, ...lowSamples].slice(0, 5) });

        const saleActivities = (d.recent_activity ?? []).filter((a) => a.type === "sale");
        setRecentOrders(
          saleActivities.slice(0, 10).map((a) => ({
            orderId: a.reference,
            customer:
              typeof a.customer === "string" && a.customer.trim() !== "" && a.customer !== "-"
                ? a.customer.trim()
                : "—",
            amount: money(a.amount ?? "0"),
            status: "Paid",
          })),
        );

        const last7Trend = (d.sales_trend.last_7_days ?? []).map((r) => ({ date: r.date, total_sales: r.total_sales }));
        const last30Trend = (d.sales_trend.last_30_days ?? []).map((r) => ({ date: r.date, total_sales: r.total_sales }));
        setSalesTrendLast7(last7Trend);
        setSalesTrendLast30(last30Trend);
        setSalesTrendRows((prev) => (prev.length ? prev : last7Trend));

        setActivityFeed(mapRecentActivityToFeed(d.recent_activity ?? []).slice(0, 5));
      } catch {
        // Keep page usable if dashboard fails
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dashboardQuery.data, datePlan]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        if (salesOverviewMode === "weekly") {
          setSalesTrendRows(salesTrendLast7);
          return;
        }
        if (salesOverviewMode === "monthly") {
          setSalesTrendRows(salesTrendLast30);
          return;
        }

        const { fromDate, toDate } = salesRange;
        if (!fromDate && !toDate) {
          setSalesTrendRows([]);
          return;
        }
        // Only fetch once a complete range is selected.
        if (!fromDate || !toDate) {
          return;
        }

        const res = await api.reports.getSalesTrend({
          token,
          filters: { from_date: fromDate, to_date: toDate },
        });
        if (cancelled) return;

        const raw = res as unknown;
        const data =
          raw && typeof raw === "object" && "data" in (raw as Record<string, unknown>)
            ? (raw as Record<string, unknown>).data
            : raw;

        const rowsCandidate =
          Array.isArray(data) ? data : data && typeof data === "object" && "rows" in (data as Record<string, unknown>) ? (data as Record<string, unknown>).rows : [];

        const rows = (Array.isArray(rowsCandidate) ? rowsCandidate : []) as Array<{ date: string; total_sales: string }>;
        setSalesTrendRows(rows);
      } catch {
        if (cancelled) return;
        setSalesTrendRows([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token, salesOverviewMode, salesRange.fromDate, salesRange.toDate, salesTrendLast7, salesTrendLast30]);

  return (
    <div className="p-4 space-y-6 md:p-6">
      {needsProfileCompletion && profileGate ? (
        <Alert className="border-amber-200 bg-amber-50">
          <AlertCircle className="h-4 w-4 text-amber-800" />
          <AlertTitle className="text-amber-900">Complete your business profile</AlertTitle>
          <AlertDescription className="text-amber-900/90 space-y-3">
            <p>
              Finish onboarding to unlock POS, inventory, and other operations. Progress:{" "}
              <span className="font-medium">{profileGate.completion}%</span>
            </p>
            <Progress value={profileGate.completion} className="h-2 bg-amber-100" />
            <Button asChild size="sm" variant="default">
              <Link to="/onboarding/business-profile">Continue setup</Link>
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Page Header */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground mb-1 md:text-3xl">Dashboard</h1>
        <p className="text-muted-foreground">
          Welcome back! Here's what's happening with your store today.
        </p>
      </div>

      {/* KPI Cards */}
      <DashboardCards cards={cards} />

      {/* Quick Actions */}
      <QuickActions />

      {/* Charts and Data */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <div className="rounded-xl border border-border bg-card p-4 min-w-0">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-foreground">Sales Overview</h2>
              <Tabs value={salesOverviewMode} onValueChange={(v) => setSalesOverviewMode(v as typeof salesOverviewMode)}>
                <TabsList>
                  <TabsTrigger value="weekly">Weekly</TabsTrigger>
                  <TabsTrigger value="monthly">Monthly</TabsTrigger>
                  <TabsTrigger value="range">Date range</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            {salesOverviewMode === "range" ? (
              <div className="mb-3">
                <ReportsDateRangeFilterBar
                  fromDate={salesRange.fromDate}
                  toDate={salesRange.toDate}
                  onChange={(r) => setSalesRange(r)}
                />
              </div>
            ) : null}

            <div className="h-[280px] w-full min-w-0 md:h-[360px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={salesTrendRows.map((r) => ({ name: r.date, sales: Number(r.total_sales) }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" stroke="#888888" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis
                    stroke="#888888"
                    fontSize={12}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(v) => formatInrThousandsTick(Number(v))}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e5e5e5",
                      borderRadius: "8px",
                    }}
                    formatter={(value: number) => [formatInrLoose(value), "Sales"]}
                  />
                  <Line type="monotone" dataKey="sales" stroke="var(--chart-1)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        <div className="min-w-0 lg:col-span-1">
          <POSQuickAccess snapshot={posSnapshot} />
        </div>
      </div>

      {/* Alerts and Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <InventoryAlerts alertSamples={inventoryAlertProps.samples} />
        <RecentActivity feed={activityFeed} />
      </div>

      {/* Table Layout */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-3">
          Recent Orders
        </h2>
        <div className="rounded-xl border border-border bg-card p-4">
          <TableLayout<Row>
            columns={tableColumns}
            rows={recentOrders}
            mobileCardRender={(row) => (
              <div className="space-y-2">
                <div className="font-medium text-foreground">{row.orderId}</div>
                <div className="text-sm text-muted-foreground">{row.customer}</div>
                <div className="flex items-center justify-between gap-2 text-sm">
                  <span>{row.amount}</span>
                  <span>{row.status}</span>
                </div>
              </div>
            )}
          />
        </div>
      </div>
    </div>
  );
}
